import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase-config.js";

export default function FeaturedYouTubeRail() {
  const [video, setVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadVideo() {
      try {
        setIsLoading(true);
        setHasError(false);

        const callable = httpsCallable(functions, "getFeaturedYouTubeVideo");
        const result = await callable();

        if (!cancelled) {
          setVideo(result?.data || null);
        }
      } catch (error) {
        console.error("Erreur getFeaturedYouTubeVideo:", error);
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadVideo();

    return () => {
      cancelled = true;
    };
  }, []);

  const embedSrc = useMemo(() => {
    if (!video?.videoId) return "";

    const origin =
      typeof window !== "undefined"
        ? encodeURIComponent(window.location.origin)
        : "";

    return (
      `https://www.youtube-nocookie.com/embed/${video.videoId}` +
      `?autoplay=1` +
      `&mute=1` +
      `&controls=0` +
      `&disablekb=1` +
      `&fs=0` +
      `&loop=1` +
      `&playlist=${video.videoId}` +
      `&playsinline=1` +
      `&rel=0` +
      `&modestbranding=1` +
      `&iv_load_policy=3` +
      `&origin=${origin}`
    );
  }, [video]);

  return (
<section className="featured-youtube-card" aria-label="Dernière vidéo YouTube">
      <div className="featured-youtube-card__inner">
        <p className="featured-youtube-card__subtitle">
          Découvrez la dernière vidéo publiée sur YouTube.
        </p>
        {isLoading && (
          <div className="featured-youtube-skeleton--full" />
        )}

        {!isLoading && hasError && (
          <div className="featured-youtube-fallback" role="status">
            Impossible de charger la vidéo YouTube pour le moment.
          </div>
        )}

        {!isLoading && !hasError && video && (
          <>
            <div className="featured-youtube-single__media">
              <iframe
                src={embedSrc}
                title={video.title}
                loading="lazy"
                allow="autoplay; encrypted-media; picture-in-picture"
                tabIndex={-1}
              />
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="featured-youtube-single__click-layer"
                aria-label={`Ouvrir la vidéo YouTube : ${video.title}`}
              />
            </div>

            <div className="featured-youtube-card__actions">
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="featured-youtube-card__button"
              >
                Voir sur YouTube
              </a>

              <h3 className="featured-youtube-single__title">{video.title}</h3>
            </div>
          </>
        )}
      </div>

      <style>{`
.featured-youtube-card {
  width: 100%;
}

        .featured-youtube-card__inner {
          display: grid;
          grid-template-columns: 1fr;
          gap: 16px;
          justify-items: center;
          align-items: center;
        }

       .featured-youtube-card__subtitle {
          margin: 0;
          color: #506866;
          font-size: 0.9rem;
          line-height: 1.6;
          text-align: center;
          text-wrap: balance;
        }

.featured-youtube-single__media {
  position: relative;
  width: 100%;
  max-width: 560px;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 18px;
  border: 1.5px solid rgba(15, 23, 42, 0.08);
  box-shadow:
    0 10px 22px rgba(15, 23, 42, 0.10),
    0 2px 6px rgba(0, 0, 0, 0.04);
  background: #eef3f1;
  isolation: isolate;
  margin: 0 auto;
}

.featured-youtube-single__media iframe {
  position: absolute;
  top: -2px;
  left: -2px;
  width: calc(100% + 4px);
  height: calc(100% + 4px);
  border: 0;
  display: block;
  pointer-events: none;
  background: #0f172a;
}

.featured-youtube-single__click-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

        .featured-youtube-single__click-layer {
          z-index: 2;
        }

        .featured-youtube-card__actions {
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          margin: 0 auto;
          text-align: center;
        }

        .featured-youtube-card__button {
          width: 100%;
          max-width: 320px;
          min-height: 46px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          padding: 11px 16px;
          font-family: var(--font-sans);
          font-size: 14px;
          font-weight: 800;
          text-decoration: none;
          cursor: pointer;
          color: white;
          background: linear-gradient(135deg, #61837A 0%, #76A5A7 100%);
          box-shadow: 0 6px 18px rgba(97, 131, 122, 0.28);
          transition:
            transform 0.18s ease,
            box-shadow 0.18s ease,
            opacity 0.18s ease;
        }

        .featured-youtube-card__button:hover {
          transform: translateY(-2px);
        }

        .featured-youtube-single__title {
          margin: 0;
          font-family: var(--font-sans);
          font-size: 13px;
          line-height: 1.45;
          font-weight: 600;
          color: #6b7280;
          max-width: 42ch;
          text-wrap: balance;
        }

        .featured-youtube-skeleton--full {
  width: 100%;
  max-width: 560px;
  aspect-ratio: 16 / 9;
          border-radius: 18px;
          background: linear-gradient(
            90deg,
            rgba(226, 232, 240, 0.9) 0%,
            rgba(241, 245, 249, 1) 50%,
            rgba(226, 232, 240, 0.9) 100%
          );
          background-size: 200% 100%;
          animation: featuredYoutubeShimmer 1.3s linear infinite;
        }

        .featured-youtube-fallback {
          width: 100%;
          max-width: 560px;
          border-radius: 16px;
          padding: 16px;
          text-align: center;
          font-size: 14px;
          font-weight: 600;
          color: #6b7280;
          background: rgba(255, 255, 255, 0.72);
          border: 1px solid rgba(15, 23, 42, 0.06);
        }

        @keyframes featuredYoutubeShimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
          
        @media (max-width: 480px) {
          .featured-youtube-card {
            border-radius: 18px;
            padding: 20px 18px;
          }

          .featured-youtube-card__inner {
            gap: 14px;
          }

          .featured-youtube-single__media,
          .featured-youtube-skeleton--full {
            border-radius: 14px;
          }

          .featured-youtube-card__subtitle,
          .featured-youtube-single__title {
            max-width: 100%;
          }
        }

        .featured-youtube-single__click-layer:focus-visible {
          outline: 2px solid #76A5A7;
          outline-offset: 4px;
          border-radius: 18px;
        }

      `}</style>
    </section>
  );
}